import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAuthStore } from '@/store/auth.store'
import { useUpdateProfile } from '../hooks/useProfile'
import { profileFormSchema, type ProfileFormData } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useTranslation } from 'react-i18next'

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'es', label: 'Español' },
] as const

export function ProfileForm() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { mutate: update, isPending, isError } = useUpdateProfile()
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      language: (user?.language as ProfileFormData['language']) ?? 'fr',
      is_profile_public: false,
    },
  })

  const onSubmit = (data: ProfileFormData) => {
    update(data, {
      onSuccess: () => {
        setSuccessMsg(t('profile.updated'))
        setTimeout(() => setSuccessMsg(null), 3000)
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <h2 className="text-base font-semibold text-neutral-900">{t('profile.personal_info')}</h2>

      {successMsg && <Alert variant="success">{successMsg}</Alert>}
      {isError && <Alert variant="error">{t('profile.generic_error')}</Alert>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label={t('profile.first_name')}
          error={errors.first_name?.message && t(errors.first_name.message)}
          {...register('first_name')}
        />
        <Input
          label={t('profile.last_name')}
          error={errors.last_name?.message && t(errors.last_name.message)}
          {...register('last_name')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-neutral-800">{t('profile.language')}</label>
        <select
          {...register('language')}
          className="min-h-[44px] rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary"
        >
          {LANGUAGES.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </div>

      <label className="flex min-h-[44px] cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          {...register('is_profile_public')}
          className="h-4 w-4 rounded border-surface-200 text-mindeia-primary focus:ring-mindeia-primary"
        />
        <span className="text-sm text-neutral-800">{t('profile.public_profile')}</span>
      </label>

      <Button type="submit" isLoading={isPending} disabled={!isDirty} className="self-start">
        {t('profile.save')}
      </Button>
    </form>
  )
}
